import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactSelect from "react-select";
import styled from "styled-components";
import { device } from "../../atoms/Utlis/media";

export const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

export type MenuCoords = { top?: number; bottom?: number; right: number };

export function usePortaledActionMenu(estimatedMenuHeight = 140) {
  const [activeMenuId, setActiveMenuId] = useState<number | null>(null);
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);
  const menuButtonRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const scrollRef = useRef<HTMLDivElement>(null);

  const closeMenu = useCallback(() => {
    setActiveMenuId(null);
    setMenuCoords(null);
  }, []);

  const handleToggleMenu = useCallback(
    (id: number) => {
      if (activeMenuId === id) {
        closeMenu();
        return;
      }

      const btn = menuButtonRefs.current[id];
      if (!btn) {
        setActiveMenuId(id);
        return;
      }

      const btnRect = btn.getBoundingClientRect();
      const gap = 8;
      const spaceBelow = window.innerHeight - btnRect.bottom;
      const spaceAbove = btnRect.top;
      const openUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
      const right = Math.max(8, window.innerWidth - btnRect.right);

      setMenuCoords(
        openUp
          ? { bottom: window.innerHeight - btnRect.top + gap, right }
          : { top: btnRect.bottom + gap, right }
      );
      setActiveMenuId(id);
    },
    [activeMenuId, closeMenu, estimatedMenuHeight]
  );

  useEffect(() => {
    if (activeMenuId === null) return;
    const scrollEl = scrollRef.current;
    scrollEl?.addEventListener("scroll", closeMenu, { passive: true });
    window.addEventListener("resize", closeMenu);
    return () => {
      scrollEl?.removeEventListener("scroll", closeMenu);
      window.removeEventListener("resize", closeMenu);
    };
  }, [activeMenuId, closeMenu]);

  return {
    activeMenuId,
    menuCoords,
    menuButtonRefs,
    scrollRef,
    handleToggleMenu,
    closeMenu,
  };
}

export const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  box-sizing: border-box;
  overflow: hidden;
  background:
    radial-gradient(ellipse 70% 40% at 0% 0%, rgba(201, 77, 170, 0.05), transparent 55%),
    radial-gradient(ellipse 50% 35% at 100% 0%, rgba(255, 201, 81, 0.06), transparent 50%),
    transparent;

  @media ${device.tablet} {
    padding: 16px;
    gap: 14px;
  }
`;

export const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);

  @media ${device.tablet} {
    padding: 16px;
    border-radius: 16px;
  }
`;

export const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const HeroLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
`;

export const BackChip = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  color: rgba(23, 23, 23, 0.72);
  transition: border-color 0.15s ease, color 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
    transform: translateY(-1px);
  }
`;

export const HeroText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-size: clamp(22px, 3vw, 28px);
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

export const PageSubtitle = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`;

export const Panel = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  overflow: hidden;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 12px;
  flex-shrink: 0;

  @media ${device.tablet} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const EntriesControl = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.62);

  select {
    height: 38px;
    border: 1px solid rgba(23, 23, 23, 0.1);
    border-radius: 10px;
    padding: 0 12px;
    background: #fafafa;
    color: #171717;
    font-family: Urbanist, sans-serif;
    font-size: 13px;
    font-weight: 600;
    outline: none;
    cursor: pointer;
    &:focus {
      border-color: rgba(201, 77, 170, 0.35);
    }
  }
`;

export const SearchControl = styled.label`
  width: min(100%, 320px);
  height: 46px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  color: rgba(23, 23, 23, 0.42);
  background: #fafafa;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:focus-within {
    border-color: rgba(201, 77, 170, 0.35);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.08);
    background: #ffffff;
  }

  input {
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-family: Montserrat, sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #171717;
  }

  @media ${device.tablet} {
    width: 100%;
  }
`;

export const ClearButton = styled.button`
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.42);
  padding: 0;
  &:hover {
    color: #171717;
  }
`;

export const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 18px 22px 22px;
  flex-shrink: 0;

  @media ${device.tablet} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const FooterText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`;

export const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

export const PageButton = styled.button<{ $active?: boolean }>`
  min-width: 36px;
  height: 36px;
  border: 1px solid ${(p) => (p.$active ? "transparent" : "rgba(23, 23, 23, 0.1)")};
  border-radius: 10px;
  padding: 0 12px;
  background: ${(p) => (p.$active ? gradient : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "rgba(23, 23, 23, 0.65)")};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${(p) => (p.$active ? "0 4px 12px rgba(201, 77, 170, 0.22)" : "none")};

  &:hover:not(:disabled) {
    border-color: rgba(201, 77, 170, 0.25);
    color: ${(p) => (p.$active ? "#fff" : "#c94daa")};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
`;

export const ModalShell = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
  overflow: hidden;
`;

/** @deprecated use ModalShell */
export const ModalCard = ModalShell;

export const ModalHero = styled.div`
  position: relative;
  overflow: hidden;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.14) 0%,
    rgba(206, 86, 176, 0.1) 55%,
    rgba(53, 183, 200, 0.06) 100%
  );
  border-bottom: 1px solid rgba(201, 77, 170, 0.1);
  flex-shrink: 0;
`;

/** @deprecated use ModalHero */
export const ModalHeader = ModalHero;

export const ModalHeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -50px;
  right: -20px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.25) 0%, transparent 70%);
`;

export const ModalHeroRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

export const ModalHeroText = styled.div`
  min-width: 0;
`;

export const ModalHeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #c94daa;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

export const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.58);
`;

export const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.25);
  }
`;

/** @deprecated use CloseBtn */
export const CloseButton = CloseBtn;

export const ModalBody = styled.div`
  padding: 20px 24px 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media ${device.tablet} {
    padding: 16px;
  }
`;

export const FormCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04);
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media ${device.tablet} {
    grid-template-columns: 1fr;
  }
`;

export const FieldGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FieldLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const inputStyles = `
  width: 100%;
  box-sizing: border-box;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  color: #171717;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:focus {
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }

  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
  }
`;

export const FieldInput = styled.input`
  ${inputStyles}
  height: 44px;
  padding: 0 14px;
`;

export const FieldTextArea = styled.textarea`
  ${inputStyles}
  min-height: 112px;
  resize: vertical;
  padding: 12px 14px;
`;

const fieldSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "12px",
    borderColor: state.isFocused ? "rgba(201, 77, 170, 0.4)" : "rgba(23, 23, 23, 0.1)",
    background: state.isFocused ? "#ffffff" : "#fafafa",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(201, 77, 170, 0.1)" : "none",
    color: "#171717",
    fontFamily: "Urbanist, sans-serif",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
    "&:hover": {
      borderColor: "rgba(201, 77, 170, 0.35)",
      background: "#ffffff",
    },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "0 14px",
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "rgba(23, 23, 23, 0.35)",
    fontWeight: 500,
  }),
  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "#171717",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isFocused ? "#c94daa" : "rgba(23, 23, 23, 0.45)",
    padding: "0 12px 0 8px",
    transition: "transform 0.15s ease, color 0.15s ease",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    "&:hover": {
      color: "#c94daa",
    },
  }),
  menu: (base: any) => ({
    ...base,
    marginTop: "8px",
    borderRadius: "16px",
    padding: "6px",
    border: "1px solid rgba(201, 77, 170, 0.12)",
    boxShadow: "0 18px 36px rgba(23, 23, 23, 0.14)",
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuList: (base: any) => ({
    ...base,
    padding: 0,
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base: any, state: any) => ({
    ...base,
    borderRadius: "12px",
    padding: "11px 13px",
    fontFamily: "Urbanist, sans-serif",
    fontSize: "14px",
    fontWeight: state.isSelected ? 700 : 600,
    color: "#171717",
    background: state.isSelected
      ? "linear-gradient(135deg, rgba(255, 201, 81, 0.18), rgba(201, 77, 170, 0.16))"
      : state.isFocused
        ? "rgba(201, 77, 170, 0.08)"
        : "#ffffff",
    cursor: "pointer",
    "&:active": {
      background: "rgba(201, 77, 170, 0.14)",
    },
  }),
};

type FieldSelectProps = {
  id?: string;
  name?: string;
  value: string | number;
  onChange: (e: { target: { value: string } }) => void;
  children: React.ReactNode;
  disabled?: boolean;
};

export const FieldSelect: React.FC<FieldSelectProps> = ({ id, name, value, onChange, children, disabled }) => {
  const options: Array<{ value: string; label: React.ReactNode; isDisabled?: boolean }> = [];

  React.Children.forEach(children, (child) => {
    if (React.isValidElement<any>(child) && child.type === "option") {
      options.push({
        value: String(child.props.value),
        label: child.props.children,
        isDisabled: child.props.disabled,
      });
    } else if (Array.isArray(child)) {
      child.forEach((nestedChild) => {
        if (React.isValidElement<any>(nestedChild) && nestedChild.type === "option") {
          options.push({
            value: String(nestedChild.props.value),
            label: nestedChild.props.children,
            isDisabled: nestedChild.props.disabled,
          });
        }
      });
    }
  });

  const selectedOption = options.find((option) => option.value === String(value)) ?? null;
  const placeholderOption = options.find((option) => option.isDisabled) ?? options.find((option) => option.value === "");

  return React.createElement(ReactSelect, {
    inputId: id,
    name,
    value: selectedOption,
    onChange: (option: any) => onChange({ target: { value: option?.value ?? "" } }),
    options: options.filter((option) => !option.isDisabled),
    placeholder: placeholderOption ? String(placeholderOption.label) : "Select an option",
    styles: fieldSelectStyles,
    menuPosition: "fixed",
    menuPortalTarget: document.body,
    isSearchable: false,
    isDisabled: disabled,
  });
};

const inlineSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "38px",
    minWidth: "74px",
    borderRadius: "10px",
    borderColor: state.isFocused ? "rgba(201, 77, 170, 0.38)" : "rgba(23, 23, 23, 0.1)",
    background: state.isFocused ? "#ffffff" : "#fafafa",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(201, 77, 170, 0.08)" : "none",
    fontFamily: "Urbanist, sans-serif",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
    "&:hover": {
      borderColor: "rgba(201, 77, 170, 0.3)",
      background: "#ffffff",
    },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "0 10px",
  }),
  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isFocused ? "#c94daa" : "rgba(23, 23, 23, 0.45)",
    padding: "0 10px 0 4px",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.15s ease, color 0.15s ease",
  }),
  menu: (base: any) => ({
    ...base,
    marginTop: "8px",
    borderRadius: "14px",
    padding: "6px",
    border: "1px solid rgba(201, 77, 170, 0.12)",
    boxShadow: "0 18px 36px rgba(23, 23, 23, 0.14)",
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuList: (base: any) => ({
    ...base,
    padding: 0,
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base: any, state: any) => ({
    ...base,
    borderRadius: "10px",
    padding: "10px 12px",
    fontFamily: "Urbanist, sans-serif",
    fontSize: "13px",
    fontWeight: state.isSelected ? 700 : 600,
    color: "#171717",
    background: state.isSelected
      ? "linear-gradient(135deg, rgba(255, 201, 81, 0.18), rgba(201, 77, 170, 0.16))"
      : state.isFocused
        ? "rgba(201, 77, 170, 0.08)"
        : "#ffffff",
  }),
};

type InlineSelectProps = {
  value: string | number;
  onChange: (e: { target: { value: string } }) => void;
  children: React.ReactNode;
};

export const InlineSelect: React.FC<InlineSelectProps> = ({ value, onChange, children }) => {
  const options: Array<{ value: string; label: React.ReactNode }> = [];

  React.Children.forEach(children, (child) => {
    if (React.isValidElement<any>(child) && child.type === "option") {
      options.push({
        value: String(child.props.value),
        label: child.props.children,
      });
    } else if (Array.isArray(child)) {
      child.forEach((nestedChild) => {
        if (React.isValidElement<any>(nestedChild) && nestedChild.type === "option") {
          options.push({
            value: String(nestedChild.props.value),
            label: nestedChild.props.children,
          });
        }
      });
    }
  });

  const selectedOption = options.find((option) => option.value === String(value)) ?? null;

  return React.createElement(ReactSelect, {
    value: selectedOption,
    onChange: (option: any) => onChange({ target: { value: option?.value ?? "" } }),
    options,
    styles: inlineSelectStyles,
    menuPosition: "fixed",
    menuPortalTarget: document.body,
    isSearchable: false,
  });
};

export const ModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 24px 24px;
  flex-shrink: 0;

  @media ${device.tablet} {
    padding: 0 16px 16px;
  }
`;

export const SecondaryButton = styled.button`
  width: 100%;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #ffffff;
  color: rgba(23, 23, 23, 0.72);
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.28);
    color: #c94daa;
  }
`;

export const PrimaryButton = styled.button`
  width: 100%;
  height: 46px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.25);
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
`;

export const StatusConfirmOverlay = ModalOverlay;

export const StatusConfirmBox = styled(ModalShell)`
  max-width: 420px;
`;

export const StatusBody = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  color: rgba(23, 23, 23, 0.78);
`;

export const StatusBtnRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const StatusCancelBtn = styled(SecondaryButton)``;
export const StatusConfirmBtn = styled(PrimaryButton)``;

